import { Basic } from "../basic.js";
import { getFormattedValue, exportCSVfile, generateFileName } from "../../lib/utils.js";
import { Converter } from "../../observer/converter.js";
import { DataTypes } from "generated";
import { Inputs } from "../../lib/inputs.js";
import { deepCompare, toBool } from "../../lib/utils.js";
import { styleTabulator } from '../../styles/styleTabulator.js';

/**
 * @class MSDataTable
 * @extends Basic
 * @classdesc Таблица данных
 * */
export class MSDataTable extends Basic {
    static get observedAttributes() {
        return super.observedAttributes.concat([
            "paneldatasource",
            "ispaged",
            "headerrowstyle",
            "tablecolumns",
            "rowstyle",
            "cellsstyle",
            "fieldstyles",
            "gridtype",
            "sorter",
            "filrets",
            "disablesortingchange",
            "exportnameformat",
        ]);
    }

    constructor() {
        super();
        const shadowRoot = this.attachShadow({ mode: "open" });
        shadowRoot.innerHTML = `<style>
                        [class^="hmi-j-"], [class*=" hmi-j-"] {
                /* use !important to prevent issues with browser extensions that change fonts */
                font-family: 'hmi' !important;
                speak: none;
                font-style: normal;
                font-weight: normal;
                font-variant: normal;
                text-transform: none;
                line-height: 1;
            }
            .hmi-j-filter:before {
                content: "\\e90e";
            }
            span.disable {
                display: none;
            }
            .tabulator{
                height: inherit;
                height: -webkit-fill-available;         
            }
            .tabulator .tabulator-header .tabulator-col.tabulator-sortable .tabulator-col-title {
                padding-right: 4px; /** уменьшаем дефолтный (из библиотеки) отступ до стрелочки */
            }
        </style>
        <style></style>
        <style></style>
        <style>
            ${styleTabulator}
        </style>
        <div style="height: inherit;
        height: -webkit-fill-available;"><div id="tabulator-container"></div></div>`;
        this._main = shadowRoot.querySelector("#tabulator-container");
        this._styleHeader = shadowRoot.querySelectorAll("style")[1];
        this._styleRow = shadowRoot.querySelectorAll("style")[2];
        this._filters = {};
        this._hHeight = 0;
        this._gridtype = 0;
        this.afterSwitch = this.afterSwitch.bind(this);
        window.addEventListener("ms_message", this.afterSwitch);
        this.waitingAttributes = []
    }

    // Формат имени экспорта
    get exportnameformat() {
        return (
            this._exportNameFormat || "{Name}_{yyyy}-{MM}-{dd}-{HH}_{mm}_{ss}"
        );
    }
    set exportnameformat(value) {
        this._exportNameFormat = value;
    }

    get disablesortingchange() {
        return this._disableSortingChange ?? false;
    }

    set disablesortingchange(value) {
        this._disableSortingChange = toBool(value);

        const columnsContainter = this.table?.element.querySelector(".tabulator-headers");
        if (columnsContainter) {
            const saveStyles = (element) => ({
                className: element.className,
                inlineStyles: element.style.cssText,
                children: [...element.children].map(child => saveStyles(child))
            });
    
            const restoreStyles = (element, savedStyles) => {
                element.className = savedStyles.className;
                if (savedStyles.inlineStyles) {
                    element.style.cssText = savedStyles.inlineStyles;
                }

                [...element.children].forEach((child, index) => {
                    const childStyles = savedStyles.children[index];
                    if (childStyles) {
                        restoreStyles(child, childStyles);
                    }
                });
            };
    
            const savedHeaderStyles = saveStyles(columnsContainter);
            const updateColumnsPromises = this.table.getColumns().map(column => 
                column.updateDefinition({ headerSort: !this._disableSortingChange })
            );
            Promise.all(updateColumnsPromises).then(() => {
                restoreStyles(columnsContainter, savedHeaderStyles);
            });
        }
    }

    afterSwitch(message) {
        const data = message.data;
        switch (data.event) {
            case "afterswitch":
                if ($pm.hasPermissions) {
                    const col = this.table.getColumns();
                    for (const iterator of col) {
                        const el = iterator
                            .getElement()
                            .querySelector("span.hmi-j-filter");
                        if (el) {
                            if (
                                $pm.checkAllow(
                                    $pm.getEntryFromElement(
                                        this,
                                        "DataTableFilter"
                                    )
                                )
                            ) {
                                el.classList.remove("disable");
                            } else {
                                el.classList.add("disable");
                            }
                        }
                    }
                    this.table.redraw(true);
                }
                break;
        }
    }

    enums(type) {
        if (!this._enums) {
            this._enums = {};
        }
        if (!this._enums[type]) {
            const enumType = DataTypes.dataTypes[type];
            if (enumType?.DataType === `EnumeratedType`) {
                const en = enumType.DisplayValues;
                if (en) {
                    this._enums[type] = {};
                    for (let i = 0; i < en.length; i++) {
                        const el = en[i];
                        const index = enumType.Indexes[i];
                        this._enums[type][index] = el;
                    }
                }
            }
        }
        return this._enums[type];
    }

    connectedCallback() {
        if (typeof this.saveStateFlag === "undefined") {
            this.saveStateFlag = this.calcSaveState();
        }
        if (this.saveStateFlag) {
            this.updateItemState();
        }
    }

    disconnectedCallback() {
        window.removeEventListener("ms_message", this.afterSwitch);
        this._saveState();
        if (this.table && this.table.destroy) {
            this.tableResizeObserver.unobserve(this.table.element);
            clearTimeout(this.tableResizeObserverTimeout)
            this.table.destroy();
            this.table = null;
        }
        super.disconnectedCallback();
    }

    get paneldatasource() {
        const tmp = JSON.parse(
            JSON.stringify(
                this._tabledata && this._tabledata.value
                    ? this._tabledata.value
                    : Array.isArray(this._tabledata)
                        ? this._tabledata
                        : []
            )
        );
        for (const iterator of tmp) {
            if (typeof iterator.$__index !== "undefined")
                delete iterator.$__index;
        }
        return tmp;
    }
    set paneldatasource(v) {
        const isFirstRendering = !this._tabledata;
        if (Array.isArray(v.value)) {
            this._dataType = v.dataType;
            this._tabledata = JSON.parse(JSON.stringify(v.value));
            for (let i = 0; i < this._tabledata.length; i++) {
                this._tabledata[i]["$__index"] = i;
            }
            this._tableType = DataTypes.dataTypes[v.dataType];
            this._elementType =
                DataTypes.dataTypes[this._tableType.TypeOfElementsName];
            if (!this._tablecolumns) {
                const tablecolumns = this.getAttribute("tablecolumns");
                if (!tablecolumns) {
                    return;
                }

                this._tablecolumns = JSON.parse(tablecolumns).column;
            } 

            this._loadState();
            if (!Array.isArray(this._tablecolumns)) {
                this._tablecolumns = [this._tablecolumns];
            }

            if (!this.table) {
                this._createTable();
            }
            if(this.isTableBuilt){
                this._updateData();
                if (isFirstRendering) {
                    this.table.redraw(); // Скроллбар заголовка не всегда будет отображаться при первичном рендеринге таблицы со скроллбаром.
                    // Поэтому нужно перерисовывать таблицу, но без force = true, поскольку тогда указанные стили для заголовков будут обнулены
                }
            } else {
                this.needUpdateData = true
            }
        } else {
            this._tabledata = v.value;
        }
    }

    _createTable() {
        this.columns = [];
        this._keyColums = {};
        let tableColumns = this.getTableColumns();

        const formatter = (cell, formatterParams, onRendered) => {
            const cellField = cell.getField();
            let ft;

            if (cellField.includes(".")) {
                // Если объект имеет вложенную структуру, где поле имеет тип другой структуры,
                // то надо будет пробегаться по всему объекту:
                ft = cellField.split(".").reduce((acc, val, i) => {
                    if (i === 0) {
                        const dataType = this._elementType.Fields[val];
                        return DataTypes.dataTypes[dataType];
                    }
                    return acc.Fields[val];
                }, "");
            } else {
                ft = this._elementType.Fields[cellField];
            }

            let format =
                formatterParams.format || formatterParams.cel.valueformat;
            cell.getElement().style.textAlign =
                window._enums.AlignText[formatterParams.cel.horizontalalign];
            let val = cell.getValue();
            switch (ft) {
                case "DATE":
                    format = format ? format : "dd-MM-yyyy";
                    break;
                case "DATE_AND_TIME":
                    format = format ? format : "dd-MM-yyyy HH:mm:ss";
                    break;
                case "TIME":
                    format = format ? format : "dd.HH:mm:ss";
                    break;
                case "TOD":
                case "TIME_OF_DAY":
                    format = format ? format : "HH:mm:ss";
                    break;
                default:
                    if (this.enums(ft)) {
                        val = this.enums(ft)[val];
                    }
            }

            return getFormattedValue(format, val, ft);
        };

        const colSort = [];
        const titleFormatter = (cell, formatterParams, onRendered) => {
            if (formatterParams.cel.enablefilter) {
                if ($pm.hasPermissions) {
                    if (
                        $pm.checkAllow(
                            $pm.getEntryFromElement(this, "DataTableFilter")
                        )
                    ) {
                        return `<span class="btn hmi-j-filter"></span>${cell.getValue()}`;
                    } else {
                        return `<span class="btn hmi-j-filter disable"></span>${cell.getValue()}`;
                    }
                } else {
                    return `<span class="btn hmi-j-filter"></span>${cell.getValue()}`;
                }
            }
            return cell.getValue();
        };

        const headerstyle = this._headerstyle || JSON.parse(this.attributes.headerrowstyle.value);
        const tempEl = this.createTempEl(headerstyle);

        for (let i = 0; i < tableColumns.length; i++) {
            const tableColumn = tableColumns[i];
            if (tableColumn.sort != 0) {
                colSort.push({
                    dir: tableColumn.sort == 1 ? "asc" : "desc",
                    position: tableColumn.sortposition,
                    column: tableColumn.field,
                });
            }
            const w = Number(tableColumn.columnwidth);
            const ind = this.columns.push({
                title: tableColumn.displayname,
                field: tableColumn.field,
                visible: this._toBool(tableColumn.visibility),
                hozAlign: window._enums.AlignText[tableColumn.horizontalalign],
                formatterParams: {
                    format: tableColumn.valueformat,
                    cel: tableColumn,
                },
                titleFormatterParams: { cel: tableColumn },
                sorter: this.getColumnSorter(tableColumn.fieldtype),
                headerSort: !this.disablesortingchange,
                formatter: formatter,
                titleFormatter: titleFormatter,
            });
            if (w) {
                this.columns[ind - 1].width = w;
            } else {
                const star = tableColumn.columnwidth.split("*").length - 1;
                if (star > 0) {
                    this.columns[ind - 1].widthGrow = star;
                } else {
                    tempEl.innerText = tableColumn.displayname;
                    this.columns[ind - 1].width =
                        tempEl.getBoundingClientRect().width + 40;
                }
            }
            this._keyColums[tableColumn.field] = {
                cellsstyle: tableColumn.cellsstyle,
            };
        }

        tempEl.remove();
        colSort.sort((a, b) => a.position - b.position);
        let isInited = false;

        const columnIndexesNeedToDisable = tableColumns
            .map((x, i) => (x.disableedit ? i : -1))
            .filter(x => x !== -1);

        this.table = new Tabulator(this._main, {
            autoResize: true,
            virtualDom: true,
            virtualDomBuffer: 100,
            index: "$__index",
            layout: "fitColumns",
            locale: "ru",
            tooltips: false,
            addRowPos: "bottom",
            pagination: this._toBool(this.attributes.ispaged.value)
                ? "local"
                : false,
            paginationAddRow: "table", // нужно, чтобы исправить Issue 28992.
            // По умолчанию строки добавляются относительно текущей страницы, и, вероятно,
            // на каком-то этапе из-за асинхронности происходит Issue 28992.
            // Чтобы этого избежать, нужно добавлять строки относительно всей таблицы, а не текущей страницы
            movableColumns: true,
            columns: this.columns,
            sortOrderReverse: true,
            headerSortTristate: true,
            rowFormatter: row => {
                const cells = row.getCells();
                cells.forEach((x, i) => {
                    x.getElement().style.justifyContent =
                        x.getColumn()._column.hozAlign;
                    if (columnIndexesNeedToDisable.includes(i)) {
                        this.disableEditCell(x);
                    }
                });

                const data = row.getData();
                for (const key in data) {
                    if (
                        this._fieldstyles &&
                        this._fieldstyles.hasOwnProperty(key)
                    ) {
                        const style = this._fieldstyles[key];
                        for (const k in style) {
                            const el = style[k];
                            const resault = Converter.convert(
                                el,
                                data[key],
                                el.FieldType
                            );
                            const div = row.getElement();
                            this.setStyle(k, resault, div);
                            if (
                                k === "BorderThickness" ||
                                k === "BorderColor"
                            ) {
                                cells.forEach(cell => {
                                    const div = cell.getElement();
                                    this.setStyle(k, resault, div);
                                });
                            }
                        }
                    }
                    if (
                        this._keyColums &&
                        this._keyColums[key] &&
                        this._keyColums[key]["cellsstyle"]
                    ) {
                        if (
                            this._cellsstyle &&
                            this._cellsstyle.hasOwnProperty(
                                this._keyColums[key].cellsstyle
                            )
                        ) {
                            const style =
                                this._cellsstyle[
                                this._keyColums[key].cellsstyle
                                ];
                            for (const k in style) {
                                const cell = row.getCell(key);
                                if (cell) {
                                    const el = style[k];
                                    if (el.FieldName) {
                                        const resault = Converter.convert(
                                            el,
                                            data[el.FieldName],
                                            el.FieldType
                                        );

                                        const div = row
                                            .getCell(key)
                                            .getElement();
                                        this.setStyle(k, resault, div);
                                    } else {
                                        this.setStyle(
                                            k,
                                            style[k].Value,
                                            cell.getElement()
                                        );
                                    }
                                }
                            }
                        }
                    }
                }
                row.normalizeHeight();
            },
            langs: {
                ru: {
                    /*
                    'columns': {
                        'name': 'Название', //replace the title of column name with the value "Name";
                    },*/
                    pagination: {
                        first: "Первая", //text for the first page button
                        first_title: "Первая страница", //tooltip text for the first page button
                        last: "Последняя",
                        last_title: "Последняя страница",
                        prev: "Предыдущая",
                        prev_title: "Предыдущая страница",
                        next: "Следующая",
                        next_title: "Следующая страница",
                    },
                },
            },
        });
        this.table.on("dataSorted", (sorter, row) => {
            if (isInited) {
                const sotrOut = [];
                for (let i = 0; i < sorter.length; i++) {
                    const element = sorter[i];
                    sotrOut.push({
                        dir: sorter[i].dir,
                        column: sorter[i].field,
                    });
                }
                this.saveItemState({ sorter: sotrOut });
            }
        })
        this.table.on("cellClick", (e, cell) => {
            const fild = cell.getField();
            const row = cell.getRow().getIndex();
            const cellvalue = cell.getValue();
            const cellEl = cell.getElement();
            const r = JSON.parse(JSON.stringify(cell.getData()));
            delete r.$__index;
            let event = new CustomEvent("cellclick", {
                detail: {
                    fild: fild,
                    row: row,
                    rowvalue: r,
                    cellvalue: cellvalue,
                    el: cellEl,
                    pageX: e.pageX,
                    pageY: e.pageY,
                },
            });
            this.dispatchEvent(event);
            event = null;
        })
        this.table.on("rowDblClick", (e, row) => {
            if ($pm.hasPermissions) {
                if (
                    $pm.checkAllow(
                        $pm.getEntryFromElement(this, "DataTableEdit")
                    )
                ) {
                    this._edRec(row);
                }
            } else {
                this._edRec(row);
            }
        })
        this.table.on("tableBuilt", (data) => {
            this.table.setSort(colSort);
            this.element = this.table.element;
            this.element.style.width = this._width + "px";
            this.element.style.backgroundColor = this.getAttribute("backgroundcolor");
            this.tabulatorTable = this.element.querySelector(".tabulator-table");
            this.tableHolder = this.element.querySelector(".tabulator-tableHolder");
            this.tableHolder.style.backgroundColor = this.getAttribute("backgroundcolor");
            headerstyle.backgroundcolor = this._calcGradient(headerstyle.backgroundcolor);

            const tableHeader = this.element.querySelector(".tabulator-header");
            tableHeader.style.borderColor = headerstyle.bordercolor;
            tableHeader.style.borderWidth = headerstyle.borderthickness;

            this.table.getColumns().forEach(col => {
                const header = col.getElement();
                this.styleColumnHeader(header, headerstyle);
                this.styleColumnHeaderText(header.querySelector('.tabulator-col-title'), headerstyle);
                header.querySelector(".hmi-j-filter")?.addEventListener("click", e => {
                    e.stopPropagation();
                    this.filterColumn(e, col);
                });
            });
            // Устанавливаем значения полученные в сеттерах
            if (this.backgroundColor) this.tableHolder.style.backgroundColor = this.backgroundColor;
            if(this.sorter) this.table.setSort(this.sorter);

            isInited = true;
            this.isTableBuilt = true
            if(this.needUpdateData) this._updateData();
            this.waitingAttributes.forEach((attr) => {
                this.attributeChangedCallback(attr.attrName, attr.oldVal, attr.newVal)
            })
            //нужно для корректного обновления ширины header
            this.table.redraw(true)

            this.addResizeObserver();
        })
    }

    getColumnSorter(fieldtype) {
        const type = window._enums.Types[fieldtype]?.type || DataTypes.dataTypes[fieldtype]?.DataType;
        switch(type) {
            case "boolean":
                return "boolean";
            case "number":
            case "wordNumber":
            case "date":
            case "EnumeratedType":
                return "number";
            case "string":
            default:
                return "string";
        }
    }

    styleColumnHeader(columnHeader, headerstyle) {
        const currentHeaderStyle = columnHeader.style;
        currentHeaderStyle.background = headerstyle.backgroundcolor;
        currentHeaderStyle.borderWidth = headerstyle.borderthickness / 2;
        currentHeaderStyle.borderColor = headerstyle.bordercolor;
    }

    styleColumnHeaderText(columnHeaderText, headerstyle) {
        const currentHeaderTextStyle = columnHeaderText.style;
        currentHeaderTextStyle.color = headerstyle.textcolor;
        currentHeaderTextStyle.fontSize = headerstyle.fontsize;
        currentHeaderTextStyle.fontStyle = this._toBool(headerstyle.fontitalic) ? "italic" : "normal";
        currentHeaderTextStyle.textDecoration = this._toBool(headerstyle.fontunderlined) ? "underline" : "";
        currentHeaderTextStyle.fontWeight = this._toBool(headerstyle.fontbold);
        currentHeaderTextStyle.fontFamily = headerstyle.fontname;
    }

    filterColumn(e, col) {
        const field = col.getField();
        const indexOf = this._tablecolumns.findIndex(e => e.field === field);
        let options;
        const inputNum = new Inputs(this._main);
        const title = `Фильтр для ${this._tablecolumns[indexOf].displayname}`;
        if (this._filters) {
            this._oldFilters = JSON.parse(
                JSON.stringify(this._filters)
            );
        }
        switch (this._tablecolumns[indexOf].fieldtype) {
            case "TIME":
                options = {
                    timeformat:
                        this._tablecolumns[indexOf]
                            .valueformat,
                    utc: false,
                };
                const val_min_t =
                    this._filters[field] &&
                        this._filters[field]["min"]
                        ? this._filters[field]["min"].value
                        : "";
                const val_max_t =
                    this._filters[field] &&
                        this._filters[field]["max"]
                        ? this._filters[field]["max"].value
                        : "";
                inputNum.inputBoxIntervalRange(
                    e,
                    title,
                    { min: val_min_t, max: val_max_t },
                    options,
                    res => {
                        if (res == null) return;
                        let setFilt = true;
                        let text = [];
                        if (
                            isNaN(res.min) &&
                            isNaN(res.max)
                        ) {
                            delete this._filters[field];
                            setFilt = false;
                        } else {
                            this._filters[field] = {};
                            if (!isNaN(res.min)) {
                                this._filters[field][
                                    "min"
                                ] = {
                                    field: field,
                                    type: ">=",
                                    value: res.min,
                                };
                                text.push(`>= ${res.min}`);
                            }
                            if (!isNaN(res.max)) {
                                this._filters[field][
                                    "max"
                                ] = {
                                    field: field,
                                    type: "<=",
                                    value: res.max,
                                };
                                text.push(`<= ${res.max}`);
                            }
                        }
                        this._setFilter(
                            col,
                            setFilt,
                            text.join(", ")
                        );
                    }
                );
                break;
            case "TOD":
                if (!options)
                    options = {
                        datetimetype: 3,
                        utc: false,
                    };
            case "DT":
                if (!options)
                    options = {
                        datetimetype: 1,
                        utc: false,
                    };
            case "DATE":
                if (!options)
                    options = {
                        datetimetype: 0,
                        utc: false,
                    };
                const val_min_dt =
                    this._filters[field] &&
                        this._filters[field]["min"]
                        ? this._filters[field]["min"].value
                        : "";
                const val_max_dt =
                    this._filters[field] &&
                        this._filters[field]["max"]
                        ? this._filters[field]["max"].value
                        : "";
                inputNum.inputBoxDataRange(
                    e,
                    title,
                    { min: val_min_dt, max: val_max_dt },
                    options,
                    res => {
                        if (res == null) return;
                        let setFilt = true;
                        let text = [];
                        if (
                            typeof res.min !== "number" &&
                            typeof res.max !== "number"
                        ) {
                            delete this._filters[field];
                            setFilt = false;
                        } else {
                            this._filters[field] = {};
                            if (
                                typeof res.min === "number"
                            ) {
                                this._filters[field][
                                    "min"
                                ] = {
                                    field: field,
                                    type: ">=",
                                    value: res.min,
                                };
                                text.push(`>= ${res.min}`);
                            }
                            if (
                                typeof res.max === "number"
                            ) {
                                this._filters[field][
                                    "max"
                                ] = {
                                    field: field,
                                    type: "<=",
                                    value: res.max,
                                };
                                text.push(`<= ${res.max}`);
                            }
                        }
                        this._setFilter(
                            col,
                            setFilt,
                            text.join(", ")
                        );
                    }
                );
                break;
            case "STRING":
                const val_str = this._filters[field]
                    ? this._filters[field]["val"].value
                    : "";
                inputNum.inputBoxText(
                    e,
                    title,
                    val_str,
                    res => {
                        if (res == null) return;
                        let setFilt = true;
                        if (res == "") {
                            delete this._filters[field];
                            setFilt = false;
                        } else {
                            this._filters[field] = {
                                val: {
                                    field: field,
                                    type: "like",
                                    value: res,
                                },
                            };
                        }

                        this._setFilter(
                            col,
                            setFilt,
                            `содержит "${res}"`
                        );
                    }
                );
                break;
            case "BOOL":
                const val_bool = this._filters[field]
                    ? this._filters[field]["val"].value
                    : "all";
                const opt = [
                    { value: "true", name: "Да" },
                    { value: "false", name: "Нет" },
                    { value: "all", name: "Все" },
                ];
                inputNum.inputBoxSelect(
                    e,
                    title,
                    opt,
                    val_bool.toString(),
                    res => {
                        if (res == null) return;
                        let setFilt = true;
                        if (res == "" || res == "all") {
                            delete this._filters[field];
                            setFilt = false;
                        } else {
                            this._filters[field] = {
                                val: {
                                    field: field,
                                    type: "=",
                                    value: this._toBool(
                                        res
                                    ),
                                },
                            };
                        }
                        this._setFilter(
                            col,
                            setFilt,
                            `равно ${res}`
                        );
                    }
                );
                break;
            case "BYTE":
            case "DINT":
            case "INT":
            case "LINT":
            case "SINT":
            case "UDINT":
            case "UINT":
            case "ULINT":
            case "USINT":
            case "REAL":
            case "LREAL":
            case "WORD":
            case "LWORD":
            case "DWORD":
                const val_min =
                    this._filters[field] &&
                        this._filters[field]["min"]
                        ? this._filters[field]["min"].value
                        : "";
                const val_max =
                    this._filters[field] &&
                        this._filters[field]["max"]
                        ? this._filters[field]["max"].value
                        : "";
                const { min, max } = window._enums.Types[this._tablecolumns[indexOf].fieldtype].description;
                inputNum.inputBoxNumberRange(
                    e,
                    title,
                    { min: val_min, max: val_max },
                    res => {
                        if (res == null) return;
                        let setFilt = true;
                        let text = [];
                        if (
                            typeof res.min !== "number" &&
                            typeof res.max !== "number"
                        ) {
                            delete this._filters[field];
                            setFilt = false;
                        } else {
                            this._filters[field] = {};
                            if (
                                typeof res.min === "number"
                            ) {
                                this._filters[field][
                                    "min"
                                ] = {
                                    field: field,
                                    type: ">=",
                                    value: res.min,
                                };
                                text.push(`>= ${res.min}`);
                            }
                            if (
                                typeof res.max === "number"
                            ) {
                                this._filters[field][
                                    "max"
                                ] = {
                                    field: field,
                                    type: "<=",
                                    value: res.max,
                                };
                                text.push(`<= ${res.max}`);
                            }
                        }
                        this._setFilter(
                            col,
                            setFilt,
                            text.join(", ")
                        );
                    },
                    { min, max }
                );
                break;
            default:
                const en =
                    DataTypes.dataTypes[
                        this._tablecolumns[indexOf]
                            .fieldtype
                    ].DisplayValues;
                let val_enum;
                if (this._filters[field]) {
                    val_enum = new Array(en.length).fill(
                        false
                    );
                    this._filters[field].val.value.forEach(
                        el => {
                            val_enum[el] = true;
                        }
                    );
                } else {
                    val_enum = [];
                }
                inputNum.inputBoxEnum(
                    e,
                    title,
                    en,
                    val_enum,
                    null,
                    res => {
                        let setFilt = true;
                        const isFilt = res.reduce(
                            (acc, val) => (acc += val)
                        );
                        delete this._filters[field];
                        if (
                            isFilt > 0 &&
                            isFilt < en.length
                        ) {
                            this._filters[field] = {};
                            const inA = [];
                            for (
                                let i = 0;
                                i < res.length;
                                i++
                            ) {
                                if (res[i]) {
                                    inA.push(i);
                                }
                            }
                            this._filters[field]["val"] = {
                                field: field,
                                type: "in",
                                value: inA,
                            };
                        }
                        this._setFilter(col, setFilt);
                    }
                );
        }
    }

    addResizeObserver() {
        // При изменении видимости родительского окна, табулатор удаляет строки, если окно скрывается,
        // и добавляет, если окно снова отображается.
        // Но при этом, если таблица была проскроллена, табулатор забывает убрать visibility: hidden у контейнера строк,
        // а также сбросить ещё некоторые внутренние настройки, что приводит к тому, что таблица становится пустой.
        // Чтобы этого избежать, нужно перерисовывать таблицу, если произошло изменение видимости окна.
        this.tableResizeObserver = new ResizeObserver((entry) => {
            if (this.tabulatorTable.style.visibility === 'hidden') {
                this.tableResizeObserverTimeout = setTimeout(() => {
                    this.table.redraw(true);
                }, 500);
            }
        });
        this.tableResizeObserver.observe(this.table.element);
    }

    afterInitialize() {
        if (!this._tabledata) {
            return
        }
        setTimeout(() => this.table.redraw(true));
    }

    getTableColumns() {
        if (this._tablecolumns) {
            return this._tablecolumns;
        }

        const columns = this.getAttribute("tablecolumns");
        const tableColumns = columns ? JSON.parse(columns).column : [];
        return !Array.isArray(tableColumns) ? [tableColumns] : tableColumns;
    }

    disableEditCell(cell) {
        cell.getElement().classList.add("tabulator-cell--disabled");
    }

    DownloadCsv() {
        const fileName =
            generateFileName(
                this.exportnameformat,
                this.elname,
                this.objectid
            ) + ".csv";

        if ($pm.hasPermissions) {
            const options = {
                cancel: () => { },
                customText: window.savePath
                ? `${i18next.t("datatable_export_data")} : ${this.elname} (${window.savePath}${fileName})${window.saveDeviceDesc}`
                : `${i18next.t("datatable_export_data")} : ${this.elname}`,
        };
            $pm.resolveControl(
                this,
                "DataTableSave",
                this._DownloadCsv.bind(this, fileName),
                options
            );
        } else this._DownloadCsv(fileName);
    }

    _DownloadCsv(fileName) {
        const resultTable = this.table.getRows().map(x => x.getCells().map(y => y.getElement().textContent));
        let res = "\uFEFF";
        const headerRow = this.getTableColumns().map(col => col.displayname).join(";") + "\n";
        res += headerRow;
        for (const row of resultTable) {
            res += `${row.join(";")}\n`;
        }
        exportCSVfile(res, fileName);
    }

    createTempEl(headerstyle) {
        const tempEl = document.createElement("span");
        const tempElStyle = tempEl.style;
        tempElStyle.fontSize = headerstyle.fontsize;
        tempElStyle.fontStyle = this._toBool(headerstyle.fontitalic)
            ? "italic"
            : "normal";
        tempElStyle.textDecoration = this._toBool(headerstyle.fontunderlined)
            ? "underline"
            : "";
        tempElStyle.fontWeight = this._toBool(headerstyle.fontbold);
        tempElStyle.fontFamily = headerstyle.fontname;
        document.body.appendChild(tempEl);
        return tempEl;
    }

    _setFilter(col, setFilt, text) {
        if ($pm.hasPermissions) {
            const inTable = this._tablecolumns.find(el => {
                return el.field === col.getField();
            });
            const options = {
                cancel: () => {
                    this._filters = this._oldFilters;
                },
                customText: `${this.elname}  ${setFilt
                    ? `${i18next.t("datatable_columnFilterEnabled")} ${inTable.displayname} : ${text}`
                    : `${i18next.t("datatable_columnFilterDisabled")} ${inTable.displayname}`
                    }`,
            };
            $pm.resolveControl(
                this,
                "DataTableFilter",
                this._successSetFilter.bind(this, col, setFilt),
                options
            );
        } else {
            this._successSetFilter(col, setFilt);
        }
    }

    _setFilterToTable() {
        this.table.clearFilter();
        for (const key in this._filters) {
            const el = this._filters[key];
            for (const k in el) {
                const e = el[k];
                this.table.addFilter(e.field, e.type, e.value);
            }
        }
    }

    _successSetFilter(col, setFilt) {
        this.saveItemState({ filrets: this._filters });
        this._setFilterToTable();
        if (setFilt) {
            if (this._headerstyle && this._headerstyle.backgroundcolorfilter) {
                col.getElement().style.backgroundColor =
                    this._headerstyle.backgroundcolorfilter;
                col.getElement().firstChild.firstChild.style.color =
                    this._headerstyle.textcolorfilter;
            } else {
                col.getElement().style.filter = `brightness(${this._headerAkcent})`;
            }
        } else {
            if (this._headerstyle && this._headerstyle.backgroundcolorfilter) {
                col.getElement().style.backgroundColor =
                    this._headerstyle.backgroundcolor;
                col.getElement().firstChild.firstChild.style.color =
                    this._headerstyle.textcolor;
            } else {
                col.getElement().style.filter = "";
            }
        }
    }

    _updateData() {
        let s;
        this.table.blockRedraw();
        if (this._tabledata.length < this.table.getDataCount()) {
            const offset = this.table.getDataCount() - this._tabledata.length;
            for (let i = 0; i < offset; i++) {
                this.table.deleteRow(this.table.getDataCount() - 1);
            }
        }

        this.table.updateOrAddData(this._tabledata);
        const filters = this.table.getFilters();

        if (filters.length > 0) {
            this.table.clearFilter();
            this.table.setFilter(filters);
            s = this.table.element.getElementsByClassName(
                "tabulator-tableHolder"
            )[0].scrollTop;
        }
        const sorter = this.table.getSorters();
        if (sorter.length > 0) {
            this.table.setSort(sorter);
            s = this.table.element.getElementsByClassName(
                "tabulator-tableHolder"
            )[0].scrollTop;
        }
        this.table.restoreRedraw();
        if (typeof s === "number")
            this.table.element.getElementsByClassName(
                "tabulator-tableHolder"
            )[0].scrollTop = s;
    }

    setStyle(key, value, div) {
        switch (key) {
            case "FontItalic":
                div.style.fontStyle = value ? "italic" : "";
                break;
            case "FontBold":
                div.style.fontWeight = value ? "bold" : "";
                break;
            case "FontUnderlined":
                div.style.textDecoration = value ? "underline" : "";
                break;
            case "TextColor":
                value = this.tryToGetUserColor(value);
                div.style.color = value ? value : "";
                break;
            case "FontSize":
                div.style.fontSize = value ? `${value}px` : "";
                break;
            case "FontName":
                div.style.fontFamily = value ? `"${value}"` : "";
                break;
            case "BackgroundColor":
                value = this.tryToGetUserColor(value);
                div.style.backgroundColor = value ? value : "";
                break;
            case "BorderColor":
                value = this.tryToGetUserColor(value);
                div.style.borderColor = value ? value : "";
                break;
            case "BorderThickness":
                let tmp = Number(value);
                if (!isNaN(tmp)) {
                    tmp = div.classList.contains("tabulator-cell")
                        ? tmp / 2
                        : tmp;
                    div.style.borderWidth = tmp === 0 ? "0" : `${tmp}px`;
                }
                break;
            case "Flash":
                div.style.animation = value
                    ? `flash linear ${_globalParams.FlashInterval}ms infinite`
                    : "";
                break;
            default:
                break;
        }
    }

    get sorter() {
        return this._sorter;
    }
    set sorter(v) {
        this._sorter = v;
        if(!this.isTableBuilt) return
        this.table.setSort(v);
    }

    get filrets() {
        return this._filters;
    }
    set filrets(v) {
        this._filters = v;
        this._setFilterToTable();
        for (const key in this._filters) {
            const col = this.table.getColumn(key);
            if (col) {
                if (
                    this._headerstyle &&
                    this._headerstyle.backgroundcolorfilter
                ) {
                    col.getElement().style.backgroundColor =
                        this._headerstyle.backgroundcolorfilter;
                    col.getElement().firstChild.firstChild.style.color =
                        this._headerstyle.textcolorfilter;
                } else {
                    col.getElement().style.filter = `brightness(${this._headerAkcent})`;
                }
            }
        }
    }

    get gridtype() {
        return this._gridtype;
    }
    set gridtype(v) {
        this._gridtype = Number(v);
        if (this._rowstyle) this.genRowStyle();
    }

    get backgroundcolor() {
        return this._backgroundcolor || this.getAttribute("backgroundcolor");
    }
    set backgroundcolor(v) {
        this._backgroundcolor = v
        if(!this.isTableBuilt) return
        if (this.table) {
            v = this.tryToGetUserColor(v);
            this.tableHolder.childNodes[0].style.backgroundColor = v;
        }
    }

    get ispaged() {
        return this.table.pagination;
    }
    set ispaged(v) {
        if (this.table) {
            if (this._toBool(v)) this.table.options.pagination = "local";
            else this.table.options.pagination = false;
        }
    }
    get tablecolumns() {
        return this._tablecolumns;
    }
    set tablecolumns(v) {
        if (!this.table) this._createTable();
        for (let key in this._tablecolumns) {
            this.columns.push({
                title: this._tablecolumns[key].DisplayName,
                field: this._tablecolumns[key].Field,
                width: +this._tablecolumns[key].ColumnWidth,
                hozAlign:
                    window._enums.AlignHorizontal[
                    this._tablecolumns[key].HorizontalAlign
                    ],
            });
        }
        this.table.options.columns = this.columns;
    }
    get headerrowstyle() {
        this._headerstyle;
    }
    set headerrowstyle(v) {
        if (!this.table) {
            return;
        }
        try {
            this._headerstyle = JSON.parse(v);
            const color = this._colorValues(this._headerstyle.backgroundcolor);
            if (color) {
                const p = this._colourIsLight(color[0], color[1], color[2])
                    ? -1
                    : 1;
                this._headerAkcent = p ? 1.25 : "80%";
            }
            this.genHeaderstyle();
        } catch (e) {
            this._headerstyle = null;
            return;
        }
    }

    genHeaderstyle() {
        const headerHeight = this._headerstyle.height.toLowerCase();
        let styleHeader = `
        .tabulator .tabulator-header .tabulator-col {
            height: ${headerHeight};
            font-weight: ${this._headerstyle.fontbold ? 700 : 400};
        }
        .tabulator .tabulator-header .tabulator-col .tabulator-col-content {
                width: 100%;
            }
            .tabulator .tabulator-header .tabulator-col .tabulator-col-content .tabulator-col-title-holder {
                display: flex;
                justify-content: ${window._enums.AlignHorizontal[this._headerstyle.horizontalalign]};
                width: 100%;
            }
            .tabulator .tabulator-header .tabulator-col .tabulator-col-content .tabulator-col-title-holder .tabulator-col-title {
                width: auto;
                max-width: 100%;
                padding-right: 3px;
            }
            .tabulator .tabulator-header .tabulator-col .tabulator-col-content .tabulator-col-title-holder .tabulator-col-sorter {
                position: static;
    }
        `;

        if (headerHeight !== "auto") {
            styleHeader += `
                .tabulator .tabulator-header .tabulator-col .tabulator-col-content {
                    height: 100%;
                }
            `;
        }

        this._styleHeader.innerHTML = styleHeader;
    }

    get rowstyle() {
        return this._rowstyle;
    }
    set rowstyle(v) {
        try {
            this._rowstyle = JSON.parse(v);
            this.genRowStyle();
        } catch (e) {
            this._rowstyle = null;
        }
    }

    genRowStyle() {
        const rowHeight = this._rowstyle.height.toLowerCase();
        this._styleRow.innerHTML = `
        .tabulator {
            border: none;
        }

        .tabulator .tabulator-header {
            border-bottom: 1px solid #999;
            border-top: 1px solid #999;
            font-weight: ${this._rowstyle.fontbold ? 700 : 400};
        }

        .tabulator .tabulator-header .tabulator-col {
            border-right: 0.5px solid #aaa;
            border-left: 0.5px solid #aaa;
        }

        .tabulator .tabulator-header .tabulator-col:last-child {
          border-right: none;
        }

        .tabulator .tabulator-header .tabulator-col:first-child {
          border-left: none;
        }

        .tabulator .tabulator-header .tabulator-col .tabulator-col-content {
            display: flex;
        }

        .tabulator .tabulator-header .tabulator-col .tabulator-col-content .tabulator-arrow {
            position: static;
            top: auto;
            right: auto;
            margin-right: 5px;
        }

        .tabulator-row {
          position: relative;
          box-sizing: border-box;
          min-height: 0px;
          height: ${rowHeight};
          background-color: ${this._rowstyle.backgroundcolor};
          font-family: ${this._rowstyle.fontname};
          font-size: ${this._rowstyle.fontsize}px;
          color: ${this._rowstyle.textcolor};
          ${this._rowstyle.fontitalic ? "font-style: italic;" : ""}
          ${this._rowstyle.fontbold ? "font-weight: 700;" : ""}
          ${this._gridtype === 1 || this._gridtype === 2
                ? `border-bottom: ${this._rowstyle.borderthickness}px solid ${this._rowstyle.bordercolor};`
                : `border-bottom: none;`
            };
          ${this._gridtype === 1 || this._gridtype === 2
                ? `border-top: ${this._rowstyle.borderthickness}px solid ${this._rowstyle.bordercolor};`
                : `border-top: none;`
            };
          ${this._rowstyle.flash
                ? `animation: flash linear ${_globalParams.FlashInterval}ms infinite`
                : ""
            }
        }

        .tabulator .tabulator-row .tabulator-cell {
            display: inline-flex;
            justify-content: center;
            align-items: center;
            ${rowHeight !== "auto" ? "height: 100%;" : ""}
          ${this._gridtype === 0 || this._gridtype === 2
                ? `border-right: ${this._rowstyle.borderthickness / 2
                }px solid ${this._rowstyle.bordercolor};`
                : `border-right: none;`
            };
          ${this._gridtype === 0 || this._gridtype === 2
                ? `border-left: ${this._rowstyle.borderthickness / 2
                }px solid ${this._rowstyle.bordercolor};`
                : `border-left: none;`
            };
        }

        .tabulator-row .tabulator-cell:last-child {
          border-right: none;
        }

        .tabulator-row .tabulator-cell:first-child {
          border-left: none;
        }

        .tabulator-row.tabulator-row-odd {
          background-color: ${this._rowstyle.backgroundcolor || "#ffffff"};
        }

        .tabulator .tabulator-row.tabulator-row-even {
            background-color: ${this._rowstyle.backgroundcoloreven || "#efefef"
            };
        }
        
        .tabulator-row.tabulator-selectable:hover {
          background-color: #bbb;
          cursor: pointer;
        }

        .tabulator-row.tabulator-row--editable {
            background-color: ${this._rowstyle.backgroundcoloredit || "rgb(187, 187, 187)"
            };
        }
        
        @keyframes flash {
            0% {
                opacity: 1;
            }
        
            50% {
                opacity: .1;
            }
        
            100% {
                opacity: 1;
            }
        }
        `;
    }

    get cellsstyle() {
        return this._cellsstyle;
    }
    set cellsstyle(v) {
        try {
            this._cellsstyle = JSON.parse(v);
        } catch (e) {
            this._cellsstyle = null;
        }
    }

    get fieldstyles() {
        return this._fieldstyles;
    }
    set fieldstyles(v) {
        try {
            this._fieldstyles = JSON.parse(v);
        } catch (e) {
            this._fieldstyles = null;
        }
    }

    _saveState() {
        const save = [];
        if (this.table && this.table.getColumnLayout) {
            const col = this.table.getColumns();
            col.forEach(el => {
                save.push({ width: el._column.width });
            });
            let headers = Number(
                /.\d/.exec(
                    this.table.element.querySelector('[role="columnheader"]')
                        .style.height
                )
            );
            const s = { col: save, header: headers };
            sessionStorage.setItem(
                `dtable_${$sw.serverState.UserData.user}_${this.id}_state`,
                JSON.stringify(s)
            );
        }
    }

    _loadState() {
        try {
            const save =
                JSON.parse(
                    sessionStorage.getItem(
                        `dtable_${$sw.serverState.UserData.user}_${this.id}_state`
                    )
                ) || [];
            for (let i = 0; i < save.col.length; i++) {
                this._tablecolumns[i].columnwidth = save.col[i].width;
            }
            this._hHeight = save.header;
        } catch (error) { }
    }

    attributeChangedCallback(attrName, oldVal, newVal) {
        if (attrName.indexOf("tablecolumns") != -1 && this.table) {
            if(this.isTableBuilt){
                const args = attrName.split(".");
                const index = args[1];
                switch (args[2].toLowerCase()) {
                    case "displayname":
                        this.table.element.getElementsByClassName(
                            "tabulator-col-title"
                        )[index].textContent = newVal;
                        break;
                    case "visibility":
                        if (newVal) {
                            this.table.getColumns()[index].show();
                        } else {
                            this.table.getColumns()[index].hide();
                        }
                        break;
                    case "sort":
                        const colSort = [];
                        if (newVal != 0) {
                            colSort.push({
                                dir: newVal == 1 ? "asc" : "desc",
                                column: this._tablecolumns[index].field,
                            });
                            this.table.setSort(colSort);
                        } else {
                            this.table.clearSort();
                        }
                        break;
                    case "horizontalalign":
                        this._tablecolumns[index].horizontalalign = newVal;
                        this.table.redraw(true);
                        break;
                    case "valueformat":
                        this._tablecolumns[index].valueformat = newVal;
                        this.table.redraw(true);
                        break;  
                    case "columnwidth":
                        this.table.getColumns()[index]._column.setWidth(newVal)
                        this.table.getColumns()[index].getDefinition().width =
                            newVal;
                        this.table.redraw(true);
                }
                this.table.redraw();
            } else {
                this.waitingAttributes.push({attrName, oldVal, newVal})
            }
        } else if (attrName === "paneldatasource") {
            if (!deepCompare(oldVal, newVal) && typeof newVal === "object") {
                const value = JSON.parse(JSON.stringify(newVal.value || []));
                for (const iterator of value) {
                    if (typeof iterator.$__index !== "undefined")
                        delete iterator.$__index;
                }
                this[attrName] = newVal;
                if (this.prop_link[attrName]) {
                    this.prop_link[attrName](
                        this.pId ? this.pId + "/" + this.id : this.id,
                        this.Links[attrName],
                        value,
                        attrName,
                        this.ItemIndex
                    );
                }
            }
        } else {
            super.attributeChangedCallback(attrName, oldVal, newVal);
        }
    }

    _edRec(row) {
        if (
            this._openDisabled ||
            !this._tablecolumns.some(({ visibleonedit }) => visibleonedit)
        ) {
            return;
        }

        this._openDisabled = true;
        const rowEl = row.getElement();
        this.setRowEditable(rowEl, true);

        const item = row.getData();
        const elRect = this.getBoundingClientRect();
        const sc = elRect.width / this.width;
        const inputs = new Inputs(this._in);
        const html = this._generateForm(item);
        inputs.inputBoxCustom(
            null,
            html,
            null,
            v => {
                this.setRowEditable(rowEl, false);
                if ($pm.hasPermissions) {
                    const form = v.querySelector("form");
                    const fields = JSON.parse(form.dataset.fields).map(el => {
                        return el.split(".")[0];
                    });
                    const data = this._extractValue(form, fields, item);
                    const items = {};
                    for (let i = 0; i < fields.length; i++) {
                        items[fields[i]] = data[fields[i]];
                    }
                    const txtFildList = [];
                    for (const key in items) {
                        if (items.hasOwnProperty(key)) {
                            if (!key.startsWith("$")) {
                                txtFildList.push(` ${key}=${items[key]}`);
                            }
                        }
                    }
                    const option = {
                        customText: `${this.elname
                        } ${i18next.t("datatable_changing_an_entry")} :${txtFildList.join(",")}`,
                    };
                    $pm.resolveControl(
                        this,
                        "DataTableEdit",
                        this._successEdit.bind(this, v, item),
                        option
                    );
                } else {
                    this._successEdit(v, item);
                }
            },
            {
                toBody: false,
                alignToElement: true,
                element: this,
                showFocus: false,
                msPopup: true,
                scale: sc,
                title: i18next.t("datatable_edit"),
                afterInit: nodes => {
                    const el = nodes.querySelectorAll("input[lenHelper]");
                    for (const iterator of el) {
                        iterator.oninput = this._lenHelper;
                    }
                },
                onCancel: e => {
                    this.setRowEditable(rowEl, false);
                    this._openDisabled = false;
                },
            }
        );
    }

    _successEdit(v, item) {
        let isCh = false;
        const _tabledata = JSON.parse(JSON.stringify(this._tabledata));
        const form = v.querySelector("form");
        const fields = JSON.parse(form.dataset.fields);
        this._openDisabled = false;
        const values = this._extractValue(form, fields, item);
        const ind = item.$__index;
        for (let i = 0; i < fields.length; i++) {
            const fl = fields[i];

            let tableData;
            let tempTableData;
            let tableDataKey;

            const isObjectNested = fl.split('.').length > 1;
            if (isObjectNested) {
                tableData = this.findNestedObject(this._tabledata[ind], fl);
                tempTableData = this.findNestedObject(_tabledata[ind], fl);
                tableDataKey = fl.split('.').pop();
            } else {
                tableData = this._tabledata[ind];
                tempTableData = _tabledata[ind];
                tableDataKey = fl;
            }

            if (tableData[tableDataKey] !== values[tableDataKey]) {
                isCh = true;
                tempTableData[tableDataKey] = values[tableDataKey];
            }
        }
        if (isCh) {
            for (const iterator of _tabledata) {
                if (typeof iterator.$__index !== "undefined")
                    delete iterator.$__index;
            }
            const val = {
                dataType: this._dataType,
                value: _tabledata,
            };
            this.SetParameter("paneldatasource", val, true);
        }
    }

    findNestedObject(sourceObj, keyPath) {
        const splittedPath = keyPath.split('.');
        const keyPathArr = splittedPath.slice(1, -1);
        let nestedObj = sourceObj[splittedPath[0]];
        for (let key of keyPathArr) {
            nestedObj = nestedObj[key];
        }
        return nestedObj;
    }

    setRowEditable(rowEl, isEditable) {
        rowEl.classList.toggle("tabulator-row--editable", isEditable);
    }

    _extractValue(form, fields, initialValues) {
        const res = {};
        for (let i = 0; i < fields.length; i++) {
            const el = form.querySelector(`#${fields[i]}`);
            if (!el) {
                res[fields[i]] = initialValues[fields[i]];
                continue;
            }
            let item = "";
            if (_enums.Types[el.dataset.type]) {
                switch (_enums.Types[el.dataset.type].type) {
                    case "number":
                    case "wordNumber":
                        item = Number(el.value) || 0;
                        break;
                    case "boolean":
                        item = this._toBool(el.checked);
                        break;
                    case "date":
                        item = el.value;
                        //                         if (item > 0) {
                        //                             const timeOffset = new Date(item).getTimezoneOffset() * 60000;
                        //                             item = item - timeOffset;
                        //                         }
                        break;
                    default:
                        item = el.value;
                }
            } else if (
                DataTypes.dataTypes[el.dataset.type] &&
                DataTypes.dataTypes[el.dataset.type].DataType ===
                `EnumeratedType`
            ) {
                item = Number(el.value) || 0;
            }
            res[el.id] = item;
        }
        return res;
    }

    _generateForm(values) {
        if (!values) values = {};
        let res = "";
        let fields = [];
        for (let i = 0; i < this._tablecolumns.length; i++) {
            const el = this._tablecolumns[i];
            if (!el.visibleonedit) continue;
            const disableEdit = el.disableedit;
            fields.push(el.field);
            res += "<tr>";

            let tableData;
            let tableDataKey;

            const isObjectNested = el.field.split('.').length > 1;
            if (isObjectNested) {
                tableData = this.findNestedObject(values, el.field);
                tableDataKey = el.field.split('.').pop();
            } else {
                tableData = values;
                tableDataKey = el.field;
            }
            let val = tableData[tableDataKey];

            if (_enums.Types[el.fieldtype]) {
                switch (_enums.Types[el.fieldtype].type) {
                    case "string":
                        let maxLen = "";
                        let lenHelp = "";
                        let onInput = "";
                        if (this._tablecolumns[i].valueformat) {
                            val = getFormattedValue(
                                this._tablecolumns[i].valueformat,
                                val.trimRight()
                            );
                            const tf = /^[t].?\d?$/i.test(
                                this._tablecolumns[i].valueformat
                            );
                            if (tf) {
                                const len =
                                    this._tablecolumns[i].valueformat.slice(1);
                                maxLen = len ? ` maxlength="${len}"` : "";
                                lenHelp = len
                                    ? `<span id="lh" style="color:gray;font-size:60%;">(${val.length}/${len})</span>`
                                    : "";
                                onInput = len ? ` lenHelper` : "";
                            }
                        }
                        res += `<td>${this._tablecolumns[i].displayname}</td>
                                <td ${disableEdit ? "class='disable-edit'" : ""
                            }><input${onInput} 
                                    data-type="${el.fieldtype}"
                                    type="text"
                                    id="${el.field}"
                                    name="${el.field}"
                                    value="${val || ""}"${maxLen}>
                                </td><td>${lenHelp}</td>`;
                        break;
                    case "boolean":
                        const chk = val ? " checked" : "";
                        res += `<td>${this._tablecolumns[i].displayname}</td>
                                <td ${disableEdit ? "class='disable-edit'" : ""
                            }><input 
                                    data-type="${el.fieldtype}"
                                    type="checkbox"
                                    id="${el.field}"
                                    name="${el.field}"
                                    ${chk}>
                                </td><td></td>`;
                        break;
                    case "number":
                    case "wordNumber":
                        const max =
                            _enums.Types[el.fieldtype].description.max ??
                            Infinity;
                        const min =
                            _enums.Types[el.fieldtype].description.min ??
                            -Infinity;
                        res += `<td>${this._tablecolumns[i].displayname}</td>
                                <td ${disableEdit ? "class='disable-edit'" : ""
                            }><ms-numericupdown 
                                    showbuttons="false" 
                                    fontsize="16" 
                                    data-type="${el.fieldtype}" 
                                    backgroundcolor="" 
                                    allownull="false" 
                                    id="${el.field}" 
                                    name="${el.field}" 
                                    maxdecimalplaces=100
                                    value="${val || 0}" 
                                    minvalue="${min}" 
                                    maxvalue="${max}" 
                                    style="width:100%;border:1px solid;position:relative;">
                                </ms-numericupdown></td><td></td>`;
                        break;
                    case "date":
                        // if (val > 0) {
                        //     const timeOffset = new Date(val).getTimezoneOffset() * 60000;
                        //     val = val + timeOffset;
                        // }
                        let datetimetype = 1;
                        switch (
                        _enums.Types[el.fieldtype].description.descType
                        ) {
                            case "date":
                                datetimetype = 0;
                                break;
                            case "dt":
                                datetimetype = 2;
                                break;
                            case "tod":
                                datetimetype = 3;
                                break;
                        }

                        /*  TODO Поддержка задания формата значения времени разных типов (dt, time, tod, date) в таблице
                            В dtpicker принимает формат для времени и даты отдельно.
                            В таблице формат задается в одном поле.
                            Этот код подходит только для типов tod и date в таблице
                            ${el.valueformat && datetimetype !== window._enums.DateTimeType_Full.Date_Type ? `timeformat="${el.valueformat}"` : ""}  
                            ${el.valueformat && datetimetype !== window._enums.DateTimeType_Full.Time_Type ? `dateformat="${el.valueformat}"` : ""}  
                        */
                        if (_enums.Types[el.fieldtype].description.descType !== "time") {
                            res += `<td>${this._tablecolumns[i].displayname}</td>
                                <td ${disableEdit ? "class='disable-edit'" : ""
                                }><ms-dtpicker 
                                    fontsize="16" 
                                    data-type="${el.fieldtype}" 
                                    sourceformat="${el.fieldtype}" 
                                    utc="false"
                                    backgroundcolor="" 
                                    id="${el.field}" 
                                    isreadonly="false" 
                                    allownull="true" 
                                    datetimetype="${datetimetype}" 
                                    name="${el.field}" 
                                    value="${val || ""}" 
                                    style="width:100%;border:1px solid;position:relative;">
                                </ms-dtpicker></td>
                                <td></td>`;
                        } else {
                            res += `<td>${this._tablecolumns[i].displayname}</td>
                                <td ${disableEdit ? "class='disable-edit'" : ""
                                }><ms-interval 
                                    fontsize="16" 
                                    data-type="${el.fieldtype}" 
                                    utc="false" 
                                    backgroundcolor="" 
                                    id="${el.field}" 
                                    isreadonly="false" 
                                    allownull="true" 
                                    name="${el.field}" 
                                    value="${val || ""}" 
                                    style="width:100%;border:1px solid;position:relative;">
                                </ms-interval></td>
                                <td></td>`;
                        }
                        break;
                    default:
                        break;
                }
            } else if (DataTypes.dataTypes[el.fieldtype]?.DataType === `EnumeratedType`) {
                const enumType = DataTypes.dataTypes[el.fieldtype];
                let options = "";
                for (
                    let ind = 0;
                    ind < enumType.Values.length;
                    ind++
                ) {
                    const element = enumType.Values[ind];
                    const index = enumType.Indexes[ind];
                    if (val === index)
                        options += `<option value="${index}" selected="selected">${element}</option>`;
                    else
                        options += `<option value="${index}">${element}</option>`;
                }
                res += `<td>${this._tablecolumns[i].displayname}</td>
                        <td ${disableEdit ? "class='disable-edit'" : ""
                    }><select data-type="${el.fieldtype}" id="${el.field
                    }" name="${el.field}">${options}</select></td><td></td>`;
            }
            res += "</tr>";
        }
        return `<style>
        #inputbody {
            max-height: 500px;
            overflow-y: auto;
            width: max-content;
            min-width: 320px;
            margin: 5px;
        }
        .disable-edit {
            background-color: #c0c0c0;
            pointer-events: none;
        }
        .disable-edit input {
            background-color: #c0c0c0;
            accent-color: #c0c0c0;
        }
        .disable-edit select {
            background-color: #c0c0c0;
        }
        td {
            padding: 2px 0;
        }
        tr input,select{
            width: 100%;
            border: gray 1px solid;
            font-size: 16px;
            height: 1.5em;
        }
        tr input,select :not(:last-child){
            margin: 2px 0;
        }</style>
        <form style="display: flex; flex-direction: column;" data-fields='${JSON.stringify(
            fields
        )}'><table>${res}</table></form>`;
    }

    _colorValues(color) {
        if (!color) return;
        if (color.toLowerCase() === "transparent") return [0, 0, 0, 0];
        if (color[0] === "#") {
            if (color.length < 7) {
                color =
                    "#" +
                    color[1] +
                    color[1] +
                    color[2] +
                    color[2] +
                    color[3] +
                    color[3] +
                    (color.length > 4 ? color[4] + color[4] : "");
            }
            return [
                parseInt(color.substr(1, 2), 16),
                parseInt(color.substr(3, 2), 16),
                parseInt(color.substr(5, 2), 16),
                color.length > 7 ? parseInt(color.substr(7, 2), 16) / 255 : 1,
            ];
        }
        if (color.indexOf("rgb") === -1) {
            const temp_elem = document.body.appendChild(
                document.createElement("fictum")
            );
            const flag = "rgb(1, 2, 3)";
            temp_elem.style.color = flag;
            if (temp_elem.style.color !== flag) {
                document.body.removeChild(temp_elem);
                return;
            }
            temp_elem.style.color = color;
            if (
                temp_elem.style.color === flag ||
                temp_elem.style.color === ""
            ) {
                document.body.removeChild(temp_elem);
                return;
            }
            color = getComputedStyle(temp_elem).color;
            document.body.removeChild(temp_elem);
        }
        if (color.indexOf("rgb") === 0) {
            if (color.indexOf("rgba") === -1) color += ",1";
            return color.match(/[\.\d]+/g).map(a => {
                return +a;
            });
        }
    }

    _colourIsLight(r, g, b) {
        var a = 1 - (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return a < 0.5;
    }
}